import { DateTime } from 'luxon';

export const LMS_CLASS_FILTER_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

/**
 * Moment.js startOf('week') uses Sunday for the 'en' locale.
 * Luxon startOf('week') always uses Monday (ISO 8601).
 * These helpers preserve the original Sunday-start behavior.
 */
export function startOfWeek(dt: DateTime): DateTime {
  const offset = dt.weekday % 7; // Sun(7)→0, Mon(1)→1, …, Sat(6)→6
  return dt.minus({ days: offset }).startOf('day');
}

export function endOfWeek(dt: DateTime): DateTime {
  const offset = dt.weekday % 7;
  return dt.plus({ days: 6 - offset }).endOf('day');
}

export const INSTRUCTOR_DASHBOARD_DATE_DISPLAY = 'ccc, d MMMM, yyyy, h:mm a';

const PARSE_FORMATS = [
  LMS_CLASS_FILTER_DATETIME_FORMAT,
  "yyyy-MM-dd'T'HHmmss",
  'yyyy-MM-dd',
];

export function formatLmsClassFilterDateTime(
  date: Date | string | number | null | undefined,
): string | null {
  if (date == null) return null;
  let dt: DateTime;
  if (date instanceof Date) {
    dt = DateTime.fromJSDate(date);
  } else if (typeof date === 'string') {
    dt = DateTime.fromISO(date);
    if (!dt.isValid)
      dt = DateTime.fromFormat(date, LMS_CLASS_FILTER_DATETIME_FORMAT);
  } else if (typeof date === 'number') {
    dt = DateTime.fromMillis(date);
  } else if (typeof date === 'object' && 'toDate' in date) {
    dt = DateTime.fromJSDate((date as { toDate: () => Date }).toDate());
  } else {
    return null;
  }
  return dt.isValid ? dt.toFormat(LMS_CLASS_FILTER_DATETIME_FORMAT) : null;
}

export function parseLmsClassFilterDateTime(
  value: string | null | undefined,
): Date | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const match = PARSE_FORMATS.reduce<DateTime | null>(
    (found, fmt) =>
      found ??
      (DateTime.fromFormat(value, fmt).isValid
        ? DateTime.fromFormat(value, fmt)
        : null),
    null,
  );
  if (match) return match.toJSDate();
  const iso = DateTime.fromISO(value);
  return iso.isValid ? iso.toJSDate() : undefined;
}

export function formatInstructorDashboardDateCell(
  cell: unknown,
  locale = 'en',
  timezone?: string,
): string | null {
  if (cell == null || cell === '') {
    return null;
  }
  let parsed: DateTime;
  if (cell instanceof DateTime) {
    parsed = cell;
  } else if (cell instanceof Date) {
    parsed = DateTime.fromJSDate(cell);
  } else if (typeof cell === 'object' && cell !== null && 'toDate' in cell) {
    parsed = DateTime.fromJSDate((cell as { toDate: () => Date }).toDate());
  } else {
    parsed = DateTime.fromISO(String(cell));
  }
  if (!parsed.isValid) {
    return null;
  }
  const display = timezone ? parsed.setZone(timezone) : parsed;
  return display.setLocale(locale).toFormat(INSTRUCTOR_DASHBOARD_DATE_DISPLAY);
}

export function formatInstructorDashboardDateTooltip(
  cell: unknown,
  locale = 'en',
  timezone?: string,
): string | null {
  const formatted = formatInstructorDashboardDateCell(cell, locale, timezone);
  if (!formatted) {
    return null;
  }
  const zone = timezone || DateTime.local().zoneName || 'UTC';
  return `${formatted} (${zone})`;
}
